import { useState, useEffect, useCallback, useRef } from "react";
import { getScopeSummariesForFys } from "../../carbonApi";

/**
 * Fetch the scope summary for several financial years in a single bulk request
 * — used by the "same month across years" period comparison.
 * @param {string} scopeKey - API scope key, e.g. "scope 1"
 * @param {number[]} fyIds - financial-year ids to fetch (empty = skip)
 * @param {number[]} locationIds - shared location filter
 * @returns {{ byFy: Record<number, object|null>, loading: boolean, error: string|null, refresh: () => void }}
 */
export const useScopeSummariesForFys = (scopeKey, fyIds = [], locationIds = []) => {
  const [byFy, setByFy] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const key = `${scopeKey}|${(fyIds || []).join(",")}|${(locationIds || []).join(",")}`;
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!scopeKey || !fyIds.length) {
      setByFy({});
      setError(null);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const { isSuccess, data, message } = await getScopeSummariesForFys({
        scope: scopeKey,
        fyIds,
        locationIds,
      });
      if (fetchId !== fetchIdRef.current) return;

      if (isSuccess) {
        const map = {};
        fyIds.forEach((fyid) => {
          map[fyid] = data?.[fyid]?.[scopeKey] || null;
        });
        setByFy(map);
      } else {
        setByFy({});
        setError(message || "Failed to load comparison data");
      }
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setByFy({});
      setError(err?.message || "An unexpected error occurred");
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { load(); }, [load]);

  return { byFy, loading, error, refresh: load };
};

export default useScopeSummariesForFys;
